import React, { type ReactNode, useCallback, useContext, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { Container, Spinner, Tab } from 'react-bootstrap'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import { ROUTER_BASEPATH } from '../../../common/constants'
import { createState } from '../../../common/integrations'
import { useGetCustomIntegrationQuery, useUpdateCustomIntegrationMutation } from '../../../store/customIntegration/customIntegration'
import { useGetMeQuery, useLazyGetCustomIntegrationAuthUrlQuery } from '../../../store/user/user'
import { addToast } from '../../../store/toast/toastSlice'
import { useDocumentTitle } from '../../../common/useDocumentTitle'
import { useCustomIntegrationScopes } from '../useCustomIntegrationScopes'
import { Tabs } from '../../../components/Tabs/Tabs'
import { HeaderContext } from '../../LayoutWithLeftSidebar/HeaderContext'
import { IntegrationManagementHeader } from '../../Integration/IntegrationManagement/IntegrationManagementHeader'
import { APPS_PAGE_PATH, CUSTOM_INTEGRATION_USER_INTEGRATION_TYPE, customIntegrationCallbackPath, customIntegrationDetailsPath } from '../constants'
import { Upsell } from '../../Plan/DeltaTrial/Upsell/Upsell'
import { DELTA_TRIAL_FEATURES } from '../../Plan/DeltaTrial/DeltaTrialFeatures'
import { useCustomIntegrationQuota } from '../useCustomIntegrationQuota'
import { type CustomIntegrationConnectionState, CustomIntegrationHeader } from './CustomIntegrationHeader'
import { CustomIntegrationAuthenticationTab } from './CustomIntegrationAuthenticationTab'
import { CustomIntegrationDetailsTab } from './CustomIntegrationDetailsTab'
import { CustomIntegrationToolsTab } from './CustomIntegrationToolsTab'
import { ConnectCredentialModal } from './ConnectCredentialModal'

type DetailsTabKey = 'about' | 'authentication' | 'details' | 'tools'

interface DetailsTab {
  key: DetailsTabKey
  title: string
  render: () => ReactNode
}

export const CustomIntegrationDetailsPage: React.FC = () => {
  const { customIntegrationId, tab } = useParams<{ customIntegrationId: string; tab?: string }>() as { customIntegrationId: string; tab?: string }
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const accountId = useAppSelector(selectAccountId)
  const { data: currentUser } = useGetMeQuery({ accountId })
  const { canUpdate, canDelete } = useCustomIntegrationScopes()
  const { customIntegrationsEnabled, isFetching: quotaIsFetching, quotaSuccess } = useCustomIntegrationQuota()
  const quotaMissing = quotaSuccess && !customIntegrationsEnabled
  const {
    data: customIntegration,
    isLoading,
    isError,
  } = useGetCustomIntegrationQuery({ accountId, customIntegrationId }, { skip: quotaMissing, refetchOnMountOrArgChange: true })
  const [updateCustomIntegration, { isLoading: isToggling }] = useUpdateCustomIntegrationMutation()
  const [getCustomIntegrationAuthUrl] = useLazyGetCustomIntegrationAuthUrlQuery()
  const [showConnectModal, setShowConnectModal] = useState<boolean>(false)
  useDocumentTitle(customIntegration?.name ?? 'Custom MCP')

  const { portalRef } = useContext(HeaderContext)
  const [headerContainer, setHeaderContainer] = useState<HTMLElement | null>(null)
  useEffect(() => {
    if (portalRef && portalRef.current) {
      setHeaderContainer(portalRef.current)
    }
  }, [portalRef])
  const userIntegration = currentUser?.integrations?.find(
    (integration) => integration.type === CUSTOM_INTEGRATION_USER_INTEGRATION_TYPE && String(integration.serviceId) === customIntegrationId,
  )
  const connectionState: CustomIntegrationConnectionState = userIntegration ? (userIntegration.state === 'invalid' ? 'reconnectRequired' : 'connected') : null

  const usesOAuth = customIntegration?.auth.type === 'oauth2' || customIntegration?.auth.type === 'oauth2Dcr'

  const handleConnect = useCallback(async () => {
    if (!usesOAuth) {
      setShowConnectModal(true)
      return
    }
    const origin = window.location.origin + ROUTER_BASEPATH
    const state = createState(origin + customIntegrationDetailsPath(customIntegrationId))
    const redirectUri = origin + customIntegrationCallbackPath(customIntegrationId)
    try {
      const { data } = await getCustomIntegrationAuthUrl({ accountId, customIntegrationId, redirectUri, state }).unwrap()
      window.location.assign(data.url)
    } catch {
      dispatch(addToast({ variant: 'error', title: 'Error', messages: ['Failed to start connecting the MCP server. Please try again.'] }))
    }
  }, [usesOAuth, accountId, customIntegrationId, getCustomIntegrationAuthUrl, dispatch])

  const handleToggleActive = useCallback(async () => {
    if (!customIntegration) {
      return
    }
    try {
      await updateCustomIntegration({ accountId, customIntegrationId, payload: { active: !customIntegration.active } }).unwrap()
    } catch {
      dispatch(addToast({ variant: 'error', title: 'Error', messages: ['Failed to update the MCP server. Please try again.'] }))
    }
  }, [customIntegration, updateCustomIntegration, accountId, customIntegrationId, dispatch])

  if (isLoading || quotaIsFetching) {
    return (
      <div className='d-flex align-items-center justify-content-center'>
        <Spinner />
      </div>
    )
  }
  if (quotaMissing) {
    return (
      <>
        {headerContainer ? createPortal(<IntegrationManagementHeader backTarget='app' />, headerContainer) : null}
        <Upsell feature={DELTA_TRIAL_FEATURES['customIntegrations']} />
      </>
    )
  }
  if (isError || !customIntegration) {
    return <Navigate to={APPS_PAGE_PATH} replace />
  }

  const canManage = customIntegration.owned && canUpdate
  const description = customIntegration.description?.trim() ?? ''
  const versionKey = `${customIntegration.id}-${customIntegration.updatedAt ?? ''}`
  const tabs: DetailsTab[] = [
    ...(description !== '' ? [{ key: 'about' as const, title: 'About', render: () => <p className='white-space-pre-line mb-0'>{description}</p> }] : []),
    ...(canManage
      ? [
          {
            key: 'details' as const,
            title: 'Details',
            render: () => <CustomIntegrationDetailsTab key={versionKey} customIntegration={customIntegration} canDelete={canDelete} />,
          },
        ]
      : []),
    {
      key: 'authentication',
      title: 'Authentication',
      render: () => <CustomIntegrationAuthenticationTab key={versionKey} customIntegration={customIntegration} readOnly={!canManage} />,
    },
    {
      key: 'tools',
      title: 'Tools',
      render: () => <CustomIntegrationToolsTab customIntegration={customIntegration} canRefresh={canManage} isConnected={connectionState === 'connected'} />,
    },
  ]
  const activeTab: DetailsTabKey = tabs.find((detailsTab) => detailsTab.key === tab)?.key ?? tabs[0].key

  const handleTabSelect = (key: string | null) => {
    if (key && key !== activeTab) {
      navigate(customIntegrationDetailsPath(customIntegrationId, key), { replace: true })
    }
  }

  const tabsContent = (
    <Tabs className='zocks-mb-2' id='custom-integration-tabs' activeKey={activeTab} onSelect={handleTabSelect}>
      {tabs.map(({ key, title, render }) => (
        <Tab key={key} eventKey={key} title={title}>
          {activeTab === key ? render() : null}
        </Tab>
      ))}
    </Tabs>
  )

  return (
    <div className='full-size-content'>
      {headerContainer ? createPortal(<IntegrationManagementHeader backTarget='app' />, headerContainer) : null}
      <Container fluid className='d-flex flex-column zocks-gap-2'>
        <CustomIntegrationHeader
          customIntegration={customIntegration}
          canManage={canManage}
          isToggling={isToggling}
          onToggleActive={handleToggleActive}
          connectionState={connectionState}
          onConnect={handleConnect}
        />
        {tabsContent}
        <ConnectCredentialModal show={showConnectModal} customIntegration={customIntegration} onHide={() => setShowConnectModal(false)} />
      </Container>
    </div>
  )
}
